import React from 'react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import FundingDashboardAdvisorPanel from '../../components/funding/FundingDashboardAdvisorPanel';
import FundingMetricGrid from '../../components/funding/FundingMetricGrid';
import FundingPageHeader from '../../components/funding/FundingPageHeader';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingPipelineRibbon from '../../components/funding/FundingPipelineRibbon';
import { FundingReadinessTierBadge } from '../../components/funding/FundingReadinessScoreCard';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingDashboard } from '../../hooks/funding/useFundingDashboard';
import { FUNDGENIUS_HQ_PRODUCT_NAME, FUNDGENIUS_HQ_TAGLINE } from '../../utils/funding/constants';

const FundGeniusHQDashboard = () => {
  const { requiresAgencySelection } = useFundingWorkspace();
  const { data, loading, error, refresh } = useFundingDashboard();

  if (loading) {
    return (
      <div className="space-y-6" data-testid="fundgenius-dashboard-loading">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-28 w-full" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      </div>
    );
  }

  const metrics = data?.metrics ?? [];
  const setup = data?.setupProgress ?? { completed: 0, total: 8 };
  const progressPct = setup.total > 0 ? Math.round((setup.completed / setup.total) * 100) : 0;
  const readinessDistribution = data?.readinessDistribution ?? {};
  const recentChecks = data?.recentReadinessChecks ?? [];
  const recentDocuments = data?.recentDocumentActivity ?? [];

  return (
    <div data-testid="fundgenius-hq-dashboard">
      <FundingPageHeader
        eyebrow="/fundgenius-hq"
        title={FUNDGENIUS_HQ_PRODUCT_NAME}
        subtitle={FUNDGENIUS_HQ_TAGLINE}
        actions={(
          <>
            <Button variant="outline" className="rounded-xl">Share Intake Form</Button>
            <Button variant="outline" className="rounded-xl" onClick={refresh}>Run Analysis</Button>
            <Button className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]">New Funding Profile</Button>
          </>
        )}
      />

      <div className="fundgenius-hq-panel mb-6 flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--fg-accent)]">
            Funding command center
          </div>
          <h2 className="mt-2 text-2xl font-bold text-[var(--fg-text)]">Move clients from intake to funded</h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--fg-muted)]">
            Track readiness, documents, lender matches, application sequencing, and funded outcomes in one advisor operating system.
          </p>
        </div>
        <div className="min-w-[260px] rounded-2xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] p-4">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-[var(--fg-muted)]">Workspace setup</span>
            <strong>{setup.completed} of {setup.total}</strong>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white">
            <div
              className="h-full rounded-full bg-[var(--fg-accent)] transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      <FundingPipelineRibbon activeStage="intake" />
      <FundingMetricGrid metrics={metrics} />

      {requiresAgencySelection ? (
        <div className="mb-4 rounded-xl border border-dashed border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3 text-sm text-[var(--fg-muted)]">
          Select an agency in the header to load workspace metrics.
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {error}
        </div>
      ) : null}

      <div className="mb-4 grid gap-4 xl:grid-cols-[1.4fr_1fr]">
        <FundingPanel title="AI recommended actions" subtitle="High-priority advisor recommendations based on available data">
          <FundingDashboardAdvisorPanel recommendations={data?.advisorRecommendations ?? []} />
        </FundingPanel>
        <FundingPanel title="Pipeline health" subtitle="Stage distribution across your book">
          <div className="space-y-3">
            {Object.entries(data?.activeStageCounts ?? {}).map(([stage, count]) => (
              <div key={stage} className="flex items-center justify-between text-sm">
                <span className="capitalize text-[var(--fg-muted)]">{stage}</span>
                <span className="font-semibold text-[var(--fg-text)]">{count}</span>
              </div>
            ))}
          </div>
        </FundingPanel>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
        <FundingPanel title="Recent pipeline deals" subtitle="Latest deal stage updates and projected commissions">
          {(data?.recentDeals ?? []).length === 0 ? (
            <p className="text-sm text-[var(--fg-muted)]">No pipeline deals yet. Create a deal from an approved submission or manually.</p>
          ) : (
            <div className="space-y-3">
              {data.recentDeals.map((deal) => (
                <div
                  key={deal.id}
                  className="flex items-center justify-between rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3"
                >
                  <div>
                    <div className="font-medium text-[var(--fg-text)]">{deal.clientName || deal.fundingClientId}</div>
                    <div className="text-xs text-[var(--fg-muted)]">
                      {deal.lenderName} · {deal.productType}
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <div className="font-medium text-[var(--fg-text)]">{deal.stage}</div>
                    <div className="text-xs text-[var(--fg-accent)]">
                      {deal.expectedCommission != null
                        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(deal.expectedCommission)
                        : '—'} projected
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </FundingPanel>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_1fr]">
        <FundingPanel title="Readiness distribution" subtitle="Estimated tiers across funding clients">
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { key: 'excellent', label: 'Excellent', tier: 'Excellent' },
              { key: 'good', label: 'Good', tier: 'Good' },
              { key: 'needsWork', label: 'Needs Work', tier: 'Needs Work' },
              { key: 'notReady', label: 'Not Ready', tier: 'Not Ready' },
            ].map((item) => (
              <div key={item.key} className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <FundingReadinessTierBadge tier={item.tier} />
                  <span className="text-lg font-bold text-[var(--fg-text)]">
                    {readinessDistribution[item.key] ?? 0}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-[var(--fg-muted)]">
            Unscored clients: {readinessDistribution.unscored ?? 0}
          </p>
        </FundingPanel>

        <FundingPanel title="Recent readiness checks" subtitle="Latest estimated scores">
          {recentChecks.length === 0 ? (
            <p className="text-sm text-[var(--fg-muted)]">
              Run readiness on a funding client to populate estimated scores.
            </p>
          ) : (
            <div className="space-y-3">
              {recentChecks.map((check) => (
                <div
                  key={check.id}
                  className="flex items-center justify-between rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3"
                >
                  <div>
                    <div className="font-medium text-[var(--fg-text)]">{check.clientName || check.fundingClientId}</div>
                    <FundingReadinessTierBadge tier={check.tier} />
                  </div>
                  <div className="text-sm font-semibold text-[var(--fg-accent)]">
                    {check.totalScore}% estimated
                  </div>
                </div>
              ))}
            </div>
          )}
        </FundingPanel>
      </div>

      {recentDocuments.length > 0 ? (
        <FundingPanel title="Recent document activity" subtitle="Latest funding document updates">
          <div className="space-y-3">
            {recentDocuments.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3"
              >
                <div>
                  <div className="font-medium text-[var(--fg-text)]">{doc.clientName || doc.fundingClientId}</div>
                  <div className="text-xs text-[var(--fg-muted)]">{doc.category}</div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-medium text-[var(--fg-text)]">{doc.status}</div>
                  <div className="text-xs text-[var(--fg-muted)]">{doc.fileName || 'No file linked'}</div>
                </div>
              </div>
            ))}
          </div>
        </FundingPanel>
      ) : null}

      {(data?.recentMatches ?? []).length > 0 ? (
        <FundingPanel title="Recent matches" subtitle="Latest lender and product recommendations">
          <div className="space-y-3">
            {data.recentMatches.map((match) => (
              <div
                key={match.id}
                className="flex items-center justify-between rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3"
              >
                <div>
                  <div className="font-medium text-[var(--fg-text)]">{match.clientName || match.fundingClientId}</div>
                  <div className="text-xs text-[var(--fg-muted)]">
                    {match.lenderName} · {match.productType}
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-semibold text-[var(--fg-accent)]">{match.matchScore}% match</div>
                  <div className="text-xs text-[var(--fg-muted)]">{match.fitTier}</div>
                </div>
              </div>
            ))}
          </div>
        </FundingPanel>
      ) : null}

      {(data?.recentSubmissions ?? []).length > 0 ? (
        <FundingPanel title="Recent submission activity" subtitle="Latest lender submission updates">
          <div className="space-y-3">
            {data.recentSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="flex items-center justify-between rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3"
              >
                <div>
                  <div className="font-medium text-[var(--fg-text)]">{submission.clientName || submission.fundingClientId}</div>
                  <div className="text-xs text-[var(--fg-muted)]">
                    {submission.lenderName} · {submission.productType}
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-medium text-[var(--fg-text)]">{submission.status}</div>
                  <div className="text-xs text-[var(--fg-muted)]">{submission.documentsStatus || '—'}</div>
                </div>
              </div>
            ))}
          </div>
        </FundingPanel>
      ) : null}

      {(data?.upcomingSequenceItems ?? []).length > 0 ? (
        <FundingPanel title="Upcoming sequence actions" subtitle="Next recommended application steps">
          <div className="space-y-3">
            {data.upcomingSequenceItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3"
              >
                <div>
                  <div className="font-medium text-[var(--fg-text)]">{item.lenderName}</div>
                  <div className="text-xs text-[var(--fg-muted)]">
                    {item.phase} · {item.productType} · {item.status}
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-medium text-[var(--fg-accent)]">{item.nextAction || 'Review item'}</div>
                </div>
              </div>
            ))}
          </div>
        </FundingPanel>
      ) : null}

      {(data?.recentCommissions ?? []).length > 0 ? (
        <FundingPanel title="Recent commission activity" subtitle="Latest commission status updates">
          <div className="space-y-3">
            {data.recentCommissions.map((commission) => (
              <div
                key={commission.id}
                className="flex items-center justify-between rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3"
              >
                <div>
                  <div className="font-medium text-[var(--fg-text)]">{commission.clientName || commission.fundingClientId}</div>
                  <div className="text-xs text-[var(--fg-muted)]">
                    {commission.lenderName} · {commission.status}
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-semibold text-[var(--fg-accent)]">
                    {commission.commissionAmount != null
                      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(commission.commissionAmount)
                      : '—'}
                  </div>
                  <div className="text-xs text-[var(--fg-muted)]">{commission.payoutStatus}</div>
                </div>
              </div>
            ))}
          </div>
        </FundingPanel>
      ) : null}

      {(data?.upcomingPayouts ?? []).length > 0 ? (
        <FundingPanel title="Upcoming payouts" subtitle="Commissions with payout due dates">
          <div className="space-y-3">
            {data.upcomingPayouts.map((commission) => (
              <div
                key={commission.id}
                className="flex items-center justify-between rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3"
              >
                <div>
                  <div className="font-medium text-[var(--fg-text)]">{commission.clientName}</div>
                  <div className="text-xs text-[var(--fg-muted)]">{commission.lenderName} · {commission.status}</div>
                </div>
                <div className="text-right text-sm text-[var(--fg-muted)]">{commission.payoutDueAt}</div>
              </div>
            ))}
          </div>
        </FundingPanel>
      ) : null}

      {(data?.clawbackMonitoring ?? []).length > 0 ? (
        <FundingPanel title="Clawback monitoring" subtitle="Commissions under clawback review">
          <div className="space-y-3">
            {data.clawbackMonitoring.map((commission) => (
              <div
                key={commission.id}
                className="flex items-center justify-between rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 dark:border-orange-900/40 dark:bg-orange-950/20"
              >
                <div>
                  <div className="font-medium text-[var(--fg-text)]">{commission.clientName}</div>
                  <div className="text-xs text-[var(--fg-muted)]">
                    {commission.lenderName} · {commission.clawbackStatus}
                  </div>
                </div>
                <div className="text-right text-sm text-orange-700 dark:text-orange-200">{commission.clawbackRisk} risk</div>
              </div>
            ))}
          </div>
        </FundingPanel>
      ) : null}

      {(data?.upcomingDealTasks ?? []).length > 0 ? (
        <FundingPanel title="Upcoming pipeline tasks" subtitle="Next actions across active deals">
          <div className="space-y-3">
            {data.upcomingDealTasks.map((deal) => (
              <div
                key={deal.id}
                className="flex items-center justify-between rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3"
              >
                <div>
                  <div className="font-medium text-[var(--fg-text)]">{deal.nextTask}</div>
                  <div className="text-xs text-[var(--fg-muted)]">
                    {deal.clientName} · {deal.lenderName} · {deal.stage}
                  </div>
                </div>
                <div className="text-right text-sm text-[var(--fg-muted)]">{deal.nextTaskDueAt}</div>
              </div>
            ))}
          </div>
        </FundingPanel>
      ) : null}

      {(data?.upcomingFollowUps ?? []).length > 0 ? (
        <FundingPanel title="Upcoming follow-ups" subtitle="Submission follow-up dates">
          <div className="space-y-3">
            {data.upcomingFollowUps.map((submission) => (
              <div
                key={submission.id}
                className="flex items-center justify-between rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3"
              >
                <div>
                  <div className="font-medium text-[var(--fg-text)]">{submission.clientName || submission.fundingClientId}</div>
                  <div className="text-xs text-[var(--fg-muted)]">{submission.lenderName} · {submission.status}</div>
                </div>
                <div className="text-right text-sm text-[var(--fg-muted)]">{submission.nextFollowUpAt}</div>
              </div>
            ))}
          </div>
        </FundingPanel>
      ) : null}
    </div>
  );
};

export default FundGeniusHQDashboard;
